import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { 
  ArrowLeft, Award, Star, Trophy, Target, Flame, 
  Calendar, Plane, CheckCircle, Moon, Sunrise, Medal,
  Crosshair, CalendarCheck, Lock
} from "lucide-react";

const iconMap = {
  Plane: Plane,
  PlaneTakeoff: Plane,
  Target: Target,
  Award: Award,
  Trophy: Trophy,
  Star: Star,
  Flame: Flame,
  Calendar: Calendar,
  CalendarCheck: CalendarCheck,
  Medal: Medal,
  Crosshair: Crosshair,
  CheckCircle: CheckCircle,
  Moon: Moon,
  Sunrise: Sunrise,
};

const BadgesPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [allBadges, setAllBadges] = useState([]);
  const [earnedBadges, setEarnedBadges] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBadges();
  }, []);

  const fetchBadges = async () => {
    try {
      const [allRes, earnedRes] = await Promise.all([
        axios.get(`${API}/badges`, { withCredentials: true }),
        axios.get(`${API}/badges/earned`, { withCredentials: true })
      ]);
      
      setAllBadges(allRes.data);
      setEarnedBadges(earnedRes.data);
    } catch (error) {
      console.error("Error fetching badges:", error);
      toast.error("Erro ao carregar badges");
    } finally {
      setLoading(false);
    }
  };

  const earnedIds = new Set(earnedBadges.map(b => b.badge_id));
  
  const categories = {
    milestone: { name: "Marcos", icon: Flag },
    training: { name: "Treino", icon: Target },
    streak: { name: "Consistência", icon: Flame },
    accuracy: { name: "Precisão", icon: Crosshair },
    special: { name: "Especiais", icon: Star },
    achievement: { name: "Conquistas", icon: Trophy },
    exam: { name: "Exames", icon: CheckCircle },
    mastery: { name: "Mestria", icon: Medal },
  };

  const groupedBadges = allBadges.reduce((acc, badge) => {
    const cat = badge.category || "other";
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(badge);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="min-h-screen bg-cockpit-bg flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cockpit-bg">
      {/* Header */}
      <header className="border-b border-cockpit-border bg-cockpit-panel/50 backdrop-blur-sm">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={() => navigate("/dashboard")}
              className="text-slate-400 hover:text-white"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="font-chivo text-lg text-cockpit-text-bright">Badges & Conquistas</h1>
              <p className="text-xs text-slate-500 font-mono">
                {earnedBadges.length} de {allBadges.length} conquistados
              </p>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-6 py-8">
        {/* Progress Summary */}
        <Card className="instrument-panel mb-8">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="font-chivo text-xl text-cockpit-text-bright">Progresso de Badges</h2>
                <p className="text-sm text-slate-400">Continue a treinar para desbloquear mais conquistas</p>
              </div>
              <div className="text-right">
                <div className="font-mono text-3xl text-orange-400 glow-orange">
                  {earnedBadges.length}/{allBadges.length}
                </div>
                <div className="text-xs text-slate-500">conquistados</div>
              </div>
            </div>
            <div className="progress-segmented">
              {[...Array(allBadges.length)].map((_, i) => (
                <div 
                  key={i} 
                  className={`progress-segment ${i < earnedBadges.length ? 'active' : ''}`}
                />
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Badge Categories */}
        <div className="space-y-8">
          {Object.entries(categories).map(([catKey, catInfo]) => {
            const badges = groupedBadges[catKey] || [];
            if (badges.length === 0) return null;
            
            const CatIcon = catInfo.icon || Award;
            
            return (
              <div key={catKey}>
                <div className="flex items-center gap-3 mb-4">
                  <CatIcon className="w-5 h-5 text-orange-400" />
                  <h3 className="font-chivo text-lg text-cockpit-text-bright">{catInfo.name}</h3>
                </div>
                
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {badges.map((badge) => (
                    <BadgeCard 
                      key={badge.badge_id} 
                      badge={badge} 
                      earned={earnedIds.has(badge.badge_id)}
                      earnedData={earnedBadges.find(e => e.badge_id === badge.badge_id)}
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </main>
    </div>
  );
};

const Flag = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"/>
    <line x1="4" y1="22" x2="4" y2="15"/>
  </svg>
);

const BadgeCard = ({ badge, earned, earnedData }) => {
  const [showDetails, setShowDetails] = useState(false);
  const Icon = iconMap[badge.icon] || Award;
  
  const requirementText = {
    "first_session": "Complete a sua primeira sessão de treino",
    "first_exam": "Complete o seu primeiro exame simulado",
    "correct_50": "Acerte 50 respostas no total",
    "correct_100": "Acerte 100 respostas no total",
    "correct_500": "Acerte 500 respostas no total",
    "streak_3": "Treine 3 dias consecutivos",
    "streak_7": "Treine 7 dias consecutivos",
    "streak_30": "Treine 30 dias consecutivos",
    "perfect_session": "Complete uma sessão com 100% de acerto",
    "exam_passed": "Passe num exame simulado (75%+)",
    "night_owl": "Treine depois da meia-noite",
    "early_bird": "Treine antes das 6h da manhã",
  };
  
  return (
    <>
      <Card 
        className={`instrument-panel transition-all cursor-pointer ${
          earned 
            ? 'border-orange-500/50 hover:border-orange-500' 
            : 'opacity-50 grayscale hover:opacity-70'
        }`}
        data-testid={`badge-${badge.badge_id}`}
        onClick={() => setShowDetails(true)}
      >
        <CardContent className="p-6 text-center">
          <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${
            earned 
              ? 'bg-gradient-to-b from-orange-500/30 to-orange-600/20 border-2 border-orange-500/50' 
              : 'bg-slate-800 border-2 border-slate-700'
          }`}>
            {earned ? (
              <Icon className="w-8 h-8 text-orange-400" />
            ) : (
              <Lock className="w-6 h-6 text-slate-600" />
            )}
          </div>
          
          <h4 className={`font-chivo text-sm mb-1 ${
            earned ? 'text-cockpit-text-bright' : 'text-slate-500'
          }`}>
            {badge.name}
          </h4>
          
          <p className="text-xs text-slate-500 mb-3">{badge.description}</p>
          
          {earned && earnedData?.earned_at && (
            <div className="text-xs text-orange-400/70 font-mono">
              {new Date(earnedData.earned_at).toLocaleDateString('pt-PT')}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Badge Details Dialog */}
      <Dialog open={showDetails} onOpenChange={setShowDetails}>
        <DialogContent className="bg-cockpit-panel border-cockpit-border">
          <DialogHeader>
            <DialogTitle className="font-chivo text-center">
              {badge.name}
            </DialogTitle>
          </DialogHeader>
          <div className="text-center py-4">
            <div className={`w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-4 ${
              earned 
                ? 'bg-gradient-to-b from-orange-500/30 to-orange-600/20 border-2 border-orange-500/50' 
                : 'bg-slate-800 border-2 border-slate-700'
            }`}>
              {earned ? (
                <Icon className="w-12 h-12 text-orange-400" />
              ) : (
                <Lock className="w-8 h-8 text-slate-600" />
              )}
            </div>
            
            <p className="text-cockpit-text-bright mb-2">{badge.description}</p>
            
            <div className="bg-slate-900/50 rounded-lg p-4 mt-4">
              <p className="text-xs text-slate-500 uppercase mb-1">Como obter</p>
              <p className="text-sm text-slate-300">
                {requirementText[badge.requirement] || badge.description}
              </p>
            </div>
            
            {earned && earnedData?.earned_at && (
              <div className="mt-4 p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
                <p className="text-xs text-emerald-400">
                  ✓ Conquistado em {new Date(earnedData.earned_at).toLocaleDateString('pt-PT', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric'
                  })}
                </p>
              </div>
            )}
            
            {!earned && (
              <div className="mt-4 p-3 bg-slate-800/50 border border-slate-700 rounded-lg">
                <p className="text-xs text-slate-500">
                  🔒 Continue a treinar para desbloquear este badge
                </p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default BadgesPage;
